import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ToolbarSubmenuProps } from '../../../types/aha-editor';
import classnames from 'classnames';
import toolbarStyles from '../../../components/Toolbar/Toolbar.module.css';
import styles from './EmojiPickerSubmenu.module.css';
import Picker from '@emoji-mart/react';
import data, { type Skin } from '@emoji-mart/data';
import WithHint from '../../../components/WithHint';
import { ReactEditor } from 'slate-react';

const EmojiPickerSubmenu: React.FC<ToolbarSubmenuProps> = ({
  editor,
  controller,
  tool,
  isSubmenu,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const onSelect = useCallback(
    (emoji: Skin) => {
      if (menuRef.current) {
        menuRef.current.dispatchEvent(
          new CustomEvent('aha-menu:close', { detail: menuRef.current })
        );
      }
      editor.insertText(emoji.native);
      ReactEditor.focus(editor);
    },
    [editor]
  );

  const theme = document?.body?.getAttribute('data-theme') ?? 'light';

  useEffect(() => {
    if (!menuRef.current) return;

    const menuOpenedHandler = (e: CustomEvent) => {
      if (e.detail === menuRef.current) {
        setShowPicker(true);
      }
    };
    const menuClosedHandler = (e: CustomEvent) => {
      if (e.detail === menuRef.current) {
        setShowPicker(false);
      }
    };

    menuRef.current.addEventListener('aha-menu:open', menuOpenedHandler);
    menuRef.current.addEventListener('aha-menu:close', menuClosedHandler);

    return () => {
      menuRef.current?.removeEventListener('aha-menu:open', menuOpenedHandler);
      menuRef.current?.removeEventListener('aha-menu:close', menuClosedHandler);
    };
  }, []);

  return (
    <aha-menu submenu={isSubmenu ? true : null} ref={menuRef}>
      <div slot="control">
        <WithHint tool={tool} toolArgs={{ editor, controller }}>
          <div
            slot="control"
            className={classnames(
              toolbarStyles.toolbarButton,
              toolbarStyles.hideCaret,
              {
                [toolbarStyles.toolbarButtonDisabled]: !tool.isEnabled({
                  editor,
                  controller,
                }),
                [styles.menuClass]:
                  controller.state.toolbarCompactLevel === 'ultra-compact',
              }
            )}
            aria-label={tool.ariaLabel}
          >
            <i className={classnames('fa-fw', tool.iconClass)} />
            {isSubmenu && tool.label}
          </div>
        </WithHint>
      </div>
      <aha-menu-content>
        <div className={styles.pickerContainer}>
          {showPicker && (
            <Picker
              data={data}
              autoFocus
              dynamicWidth
              theme={theme}
              onEmojiSelect={onSelect}
              // TODO: Frequently appears twice because emoji-mart adds it
              // unconditionally on initialize, the hack we use in whiteboards
              // does not work here on the first render, likely due to a race
              // condition, it gets added after the useEffect runs.
              // Will disable it here for now and reenable it when we have a
              // better solution.
              maxFrequentRows={0}
              style={{ height: '30px' }}
            />
          )}
        </div>
      </aha-menu-content>
    </aha-menu>
  );
};

export default EmojiPickerSubmenu;
